export const name = "TIMESTAMP_TRUNC";
export const summary = `Truncates a \`TIMESTAMP\` or \`DATETIME\` value at a particular granularity.`;
export const reference = `
## TIMESTAMP_TRUNC

Truncates a \`TIMESTAMP\` or \`DATETIME\` value at a particular granularity.

\`\`\`
TIMESTAMP_TRUNC(timestamp_value, timestamp_granularity[, time_zone])
\`\`\`

### 

\`\`\`
TIMESTAMP_TRUNC(datetime_value, datetime_granularity)
\`\`\`


### Description

Truncates a \`TIMESTAMP\` or \`DATETIME\` value at a particular granularity.


### Definitions

- \`timestamp_value\`: A \`TIMESTAMP\` value to truncate.
- \`timestamp_granularity\`: The truncation granularity for a \`TIMESTAMP\` value. Date granularities and time granularities can be used.
- \`time_zone\`: A time zone to use with the \`TIMESTAMP\` value. Time zone parts can be used. Use this argument if you want to use a time zone other than the default time zone, UTC, as part of the truncate operation.

- \`datetime_value\`: A \`DATETIME\` value to truncate.

- \`datetime_granularity\`: The truncation granularity for a \`DATETIME\` value. Date granularities and time granularities can be used.




### Date granularity definitions

- \`DAY\`: The day in the Gregorian calendar year that contains the value to truncate.

- \`WEEK\`: The first day in the week that contains the value to truncate. Weeks begin on Sundays. \`WEEK\` is equivalent to\`WEEK(SUNDAY)\`.

- \`WEEK(WEEKDAY)\`: The first day in the week that contains the value to truncate. Weeks begin on \`WEEKDAY\`. \`WEEKDAY\` must be one of the following: \`SUNDAY\`, \`MONDAY\`, \`TUESDAY\`, \`WEDNESDAY\`, \`THURSDAY\`, \`FRIDAY\`, or \`SATURDAY\`.

- \`ISOWEEK\`: The first day in the ISO 8601 week that contains the value to truncate. The ISO week begins on Monday. The first ISO week of each ISO year contains the first Thursday of the corresponding Gregorian calendar year.

- \`MONTH\`: The first day in the month that contains the value to truncate.

- \`QUARTER\`: The first day in the quarter that contains the value to truncate.

- \`YEAR\`: The first day in the year that contains the value to truncate.

- \`ISOYEAR\`: The first day in the ISO 8601 week-numbering year that contains the value to truncate. The ISO year is the Monday of the first week where Thursday belongs to the corresponding Gregorian calendar year.




### Time granularity definitions

- \`MICROSECOND\`: If used, nothing is truncated from the value.

- \`MILLISECOND\`: The nearest lesser than or equal millisecond.

- \`SECOND\`: The nearest lesser than or equal second.

- \`MINUTE\`: The nearest lesser than or equal minute.

- \`HOUR\`: The nearest lesser than or equal hour.




### Time zone part definitions

- \`MINUTE\`
- \`HOUR\`
- \`DAY\`
- \`WEEK\`
- \`WEEK(<WEEKDAY>)\`
- \`ISOWEEK\`
- \`MONTH\`
- \`QUARTER\`
- \`YEAR\`
- \`ISOYEAR\`


### Details

The resulting value is always rounded to the beginning of \`granularity\`.


### Return Data Type

The same data type as the first argument passed into this function.


### Examples



\`\`\`
SELECT
  TIMESTAMP_TRUNC(TIMESTAMP "2008-12-25 15:30:00+00", DAY, "UTC") AS utc,
  TIMESTAMP_TRUNC(TIMESTAMP "2008-12-25 15:30:00+00", DAY, "America/Los_Angeles") AS la;

-- Display of results may differ, depending upon the environment and time zone where this query was executed.
/*-------------------------+-------------------------*
 | utc | la |
 +-------------------------+-------------------------+
 | 2008-12-25 00:00:00 UTC | 2008-12-25 08:00:00 UTC |
 *-------------------------+-------------------------*/
\`\`\`

In the following example, \`timestamp_expression\` has a time zone offset of +12. The first column shows the \`timestamp_expression\` in UTC time. The second column shows the output of \`TIMESTAMP_TRUNC\` using weeks that start on Monday. Because the \`timestamp_expression\` falls on a Sunday in UTC, \`TIMESTAMP_TRUNC\`truncates it to the preceding Monday. The third column shows the same function with the optional Time zone definitionargument 'Pacific/Auckland'. Here, the function truncates the\`timestamp_expression\` using New Zealand Daylight Time, where it falls on a Monday.



\`\`\`
SELECT
  timestamp_value AS timestamp_value,
  TIMESTAMP_TRUNC(timestamp_value, WEEK(MONDAY), "UTC") AS utc_truncated,
  TIMESTAMP_TRUNC(timestamp_value, WEEK(MONDAY), "Pacific/Auckland") AS nzdt_truncated
FROM (SELECT TIMESTAMP("2017-11-06 00:00:00+12") AS timestamp_value);

-- Display of results may differ, depending upon the environment and time zone where this query was executed.
/*-------------------------+-------------------------+-------------------------*
 | timestamp_value | utc_truncated | nzdt_truncated |
 +-------------------------+-------------------------+-------------------------+
 | 2017-11-05 12:00:00 UTC | 2017-10-30 00:00:00 UTC | 2017-11-05 11:00:00 UTC |
 *-------------------------+-------------------------+-------------------------*/
\`\`\`

In the following example, the original \`timestamp_expression\` is in the Gregorian calendar year 2015. However, \`TIMESTAMP_TRUNC\` with the \`ISOYEAR\` date part truncates the \`timestamp_expression\` to the beginning of the ISO year, not the Gregorian calendar year. The first Thursday of the 2015 calendar year was 2015-01-01, so the ISO year 2015 begins on the preceding Monday, 2014-12-29. Therefore the ISO year boundary preceding the \`timestamp_expression\`2015-06-15 00:00:00+00 is 2014-12-29.



\`\`\`
SELECT
  TIMESTAMP_TRUNC("2015-06-15 00:00:00+00", ISOYEAR) AS isoyear_boundary,
  EXTRACT(ISOYEAR FROM TIMESTAMP "2015-06-15 00:00:00+00") AS isoyear_number;

-- Display of results may differ, depending upon the environment and time zone where this query was executed.
/*-------------------------+----------------*
 | isoyear_boundary | isoyear_number |
 +-------------------------+----------------+
 | 2014-12-29 00:00:00 UTC | 2015 |
 *-------------------------+----------------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/timestamp_functions
`;
